/**
 * Aufteilungsschluessel: EIN Auftrag, mehrere Rechnungen.
 *
 * Der Betrieb hat Projekte, deren Kosten sich zwei, drei oder mehr
 * Auftraggeber teilen. Rechtlich bekommt jeder von ihnen eine EIGENE Rechnung
 * mit eigener Nummer, eigenem Kundenschnappschuss und eigenem Debitor in der
 * Buchhaltung — ein Beleg mit mehreren Koepfen waere keiner. Und er hat
 * Gesamtprojekte mit mehreren Haeusern, bei denen EIN Auftraggeber je Haus
 * eine eigene Rechnung braucht. Dieses Modul liefert dafuer den Rechenkern und
 * nichts sonst: kein Supabase, kein DOM, keine Belegerzeugung.
 *
 * DIE ZENTRALE ENTSCHEIDUNG: es gibt nur EIN Primitiv, den Anteil je
 * (Position x Teilnehmer) in Basispunkten. Alle Faelle, die der Betrieb
 * unterscheidet, sind Belegungen davon:
 *
 *   Quote 50/30/20    jede Position traegt 5000 / 3000 / 2000
 *   positionsweise    Pos. 1.1 -> 10000/0/0, Pos. 1.2 -> 0/10000/0
 *   Abschnitte        wie positionsweise, nur ist `customerId` ueberall gleich
 *   gemischt          Haus A -> Kunde X, Haus B+C -> Kunde Y
 *
 * Beides zusammen (Grundquote mit Ausnahmen je Position) faellt damit von
 * selbst ab. Ein zweiter Code-Pfad je Modus haette zwei Testmatrizen und zwei
 * Gelegenheiten fuer denselben Fehler bedeutet.
 *
 * DER TEILNEHMER IST NICHT DER AUFTRAGGEBER (seit SQL Nr. 54). Er ist das Paar
 * aus Abschnitt und Auftraggeber und traegt eine eigene Kennung (`partyId`).
 * Solange die Identitaet der Auftraggeber war, liess sich das Haus-Projekt
 * nicht abbilden: derselbe Auftraggeber konnte im Schluessel nicht zweimal
 * stehen. Jede Karte in diesem Modul ist deshalb nach `partyId` geschluesselt.
 *
 * BASISPUNKTE, nicht Prozent als Gleitkommazahl: ein Drittel ist als
 * Prozentzahl nicht darstellbar, und drei Drittel muessen sich exakt zu 100 %
 * addieren. In ganzen Basispunkten ist die Pruefsumme eine Ganzzahlgleichung
 * (3334 + 3333 + 3333 = 10000) statt eines Vergleichs mit Toleranz.
 */
import type { InvoiceLine, ServiceTransaction } from '../types';
import { isAccountingEntry, isBackfilledAltEntry } from './serviceLogUtils';

/** 100,00 % in Basispunkten. */
export const SHARE_FULL_BP = 10000;

/**
 * Das Raster, auf dem geteilt wird: ZWEI Nachkommastellen.
 *
 * Geteilt wird auf einem ganzzahligen Raster und nicht auf Gleitkommazahlen —
 * nur so ist die Summe der Teilmengen EXAKT die Ausgangsmenge. Welches Raster,
 * ist eine Frage an den Beleg: eine Begehung, die auf drei Abschnitte faellt,
 * stand mit `1,334 Stück` auf der Rechnung. Drei Nachkommastellen sind eine
 * Genauigkeit, die es im Auftrag nicht gibt und die dem Empfaenger nichts
 * sagt; zwei sind die Form, in der der Betrieb Mengen fuehrt.
 *
 * Die Ausgangsmenge wird dabei NICHT gerundet — nur der Schnitt zwischen den
 * Teilnehmern wird groeber. Aus 4 Stück werden 1,34 + 1,33 + 1,33, nicht
 * dreimal 1,33: der Rest bleibt drin und geht ueber die Restvergabe an einen
 * Teilnehmer. Dreimal abrunden hiesse, 0,01 Stück dauerhaft nicht abzurechnen
 * — die Position schloesse nie, und der Integritaetslauf meldete sie fuer
 * immer.
 */
export const QUANTITY_GRID = 100;

/**
 * Das feine Raster fuer Ausgangsmengen, die auf dem groben gar nicht liegen.
 *
 * `InvoiceLine.quantity` fuehrt bis zu drei Nachkommastellen. Eine Menge wie
 * 0,125 auf dem Hundertstel-Raster zu teilen hiesse, sie vorher zu runden —
 * dann waere die Summe der Teilmengen nicht mehr die Ausgangsmenge, und genau
 * das darf dieses Modul nicht. Solche Mengen werden deshalb weiterhin auf
 * Tausendsteln geteilt.
 */
export const QUANTITY_GRID_FINE = 1000;

/**
 * Welches Raster fuer diese Menge? Das grobe, wenn sie darauf liegt.
 *
 * Die Toleranz faengt die Gleitkommadarstellung ab: 1,15 * 100 ist 114,99999…
 * und waere ohne sie eine „krumme" Menge, die ins feine Raster fiele.
 */
const gridFor = (quantity: number): number => {
    const onCoarse = quantity * QUANTITY_GRID;
    return Math.abs(onCoarse - Math.round(onCoarse)) < 1e-6 ? QUANTITY_GRID : QUANTITY_GRID_FINE;
};

/** Toleranz fuer Mengenvergleiche, wie in invoiceWriteBackLogic. */
export const SPLIT_EPSILON = 0.0005;

// ═══════════════════════════════════════════════════════════════════════════
// Der Schluessel
// ═══════════════════════════════════════════════════════════════════════════

export interface ProjectSplitParticipant {
    /**
     * DIE IDENTITAET des Teilnehmers (project_splits.id) — nicht der
     * Auftraggeber.
     *
     * Bis SQL Nr. 54 war der Auftraggeber die Identitaet, und genau das machte
     * den zweiten Fall des Betriebs unmoeglich: EIN Gesamtprojekt mit mehreren
     * Haeusern, EIN Auftraggeber, je Haus aber eine eigene Rechnung. Der
     * Teilnehmer ist deshalb das PAAR aus Abschnitt und Auftraggeber; der
     * Auftraggeber ist nur noch ein Feld daran.
     *
     * Damit fallen drei Faelle unter dasselbe Primitiv:
     *   mehrere Auftraggeber   je Auftraggeber ein Teilnehmer
     *   mehrere Abschnitte     je Haus einer, `customerId` ueberall gleich
     *   gemischt               Haus A -> Kunde X, Haus B+C -> Kunde Y
     * Und ein Haus, das sich zwei Eigentuemer teilen, sind zwei Teilnehmer mit
     * demselben `label` und je 5000 Basispunkten.
     *
     * Die Kennung wird im Client vergeben (crypto.randomUUID) und beim
     * Speichern durchgereicht. Ueber SIE laeuft der Abgleich — nicht ueber den
     * Auftraggeber und nicht ueber den Namen. An ihr haengen die Ausnahmen je
     * LV-Position; ein Teilnehmer, der beim Umbenennen neu angelegt wuerde,
     * verloere sie samt seiner gesamten positionsweisen Zuordnung.
     */
    partyId: string;
    customerId: string;
    /**
     * Der Abschnitt ("Haus 3", "BA II"). Leer heisst "kein Abschnitt" und ist
     * der Bestand: ein Projekt, das sich nur nach Auftraggebern teilt.
     *
     * Nicht bloss Anzeige: bei mehreren Belegen an DENSELBEN Auftraggeber ist
     * das Label das Einzige, was sie unterscheidet — es gehoert auf den Beleg,
     * sonst liegen vier nicht unterscheidbare Rechnungen beim Kunden.
     */
    label?: string;
    /** Grundquote in Basispunkten; gilt fuer jede Position ohne Ausnahme. */
    defaultShareBp: number;
    /**
     * Ausnahmen je LV-Position (serviceId -> Basispunkte).
     *
     * Wer rein positionsweise aufteilt, setzt die Grundquote auf 0 und traegt
     * hier jede Position ein. Wer nach Quote aufteilt, laesst das Feld leer.
     */
    overrides?: Record<string, number>;
}

export interface ProjectSplit {
    projectId: string;
    participants: ProjectSplitParticipant[];
}

/**
 * Ein Projekt ist geteilt, sobald mehr als ein Auftraggeber beteiligt ist.
 *
 * Ein einzelner Teilnehmer mit 100 % ist ausdruecklich KEIN Split: er soll
 * exakt denselben Beleg erzeugen wie ein Projekt ohne Schluessel, damit der
 * Sonderfall nicht zum Regelfall mit Sonderbehandlung wird.
 */
export const hasSplit = (split?: ProjectSplit | null): boolean =>
    (split?.participants?.length ?? 0) > 1;

/** Anteil dieses Teilnehmers an DIESER Position — Ausnahme schlaegt Grundquote. */
export const shareBpFor = (participant: ProjectSplitParticipant, serviceId?: string): number => {
    if (serviceId) {
        const override = participant.overrides?.[serviceId];
        if (override !== undefined) return override;
    }
    return participant.defaultShareBp;
};

/**
 * Der wirksame Schluessel: je Position, wer mit wie viel beteiligt ist.
 *
 * Geschluesselt nach TEILNEHMER (partyId) und nicht nach Auftraggeber: bei
 * mehreren Abschnitten desselben Auftraggebers ueberschriebe sich sonst der
 * zweite Abschnitt mit dem ersten, und die Haelfte des Projekts fiele lautlos
 * aus jeder Rechnung.
 *
 * Teilnehmer mit Anteil 0 fallen HERAUS und nicht mit einer Nullzeile hinein.
 * Eine Position ueber 0,00 EUR auf dem Beleg zeigte dem Auftraggeber ein
 * Leistungsverzeichnis, das ihn nichts angeht — dieselbe Regel wie beim
 * Preisschutz gegenueber dem Aussendienst.
 */
export const resolveShares = (
    serviceIds: readonly string[],
    split: ProjectSplit,
): Map<string, Map<string, number>> => {
    const out = new Map<string, Map<string, number>>();
    for (const serviceId of serviceIds) {
        const byParty = new Map<string, number>();
        for (const p of split.participants) {
            const bp = shareBpFor(p, serviceId);
            if (bp > 0) byParty.set(p.partyId, bp);
        }
        out.set(serviceId, byParty);
    }
    return out;
};

/**
 * Der Teilnehmer eines Belegs, einer Protokollzeile oder eines Alt-Abschlags.
 *
 * EIN Aufloeser fuer alle drei, weil alle drei dieselbe Geschichte haben: bis
 * SQL Nr. 54 trugen sie nur den Auftraggeber. Ein Beleg ist eingefroren und ein
 * Protokolleintrag Beweismittel — beide werden NICHT nachtraeglich
 * umgeschrieben. Aufgeloest wird deshalb beim Lesen, ueber `legacy`.
 *
 * Und aufgeloest wird nur, was EINDEUTIG ist (siehe legacyPartyByCustomer).
 * Hat ein Auftraggeber mehrere Abschnitte, ist die Zeile keinem davon
 * zuzuordnen; sie zaehlt dann als nicht zugeordnet und sperrt, statt zu raten.
 * Geraten waere hier eine falsche Geldforderung.
 */
export const resolveParty = (
    doc: { splitPartyId?: string; customerId?: string } | null | undefined,
    legacy: ReadonlyMap<string, string>,
): string | undefined => {
    const direct = (doc?.splitPartyId || '').trim();
    if (direct) return direct;
    const customerId = (doc?.customerId || '').trim();
    return customerId ? legacy.get(customerId) : undefined;
};

/**
 * customerId -> partyId, aber NUR wo eindeutig.
 *
 * Der Weg, auf dem Altbestand ohne `splitPartyId` weiterhin richtig zaehlt: ein
 * Projekt, das sich bisher nur nach Auftraggebern teilte, hat je Auftraggeber
 * genau einen Teilnehmer — dort ist die Zuordnung zwingend und keine Vermutung.
 *
 * Sobald ein Auftraggeber ZWEI Teilnehmer hat (er hat mehrere Abschnitte),
 * faellt er aus der Karte. Das ist der ganze Zweck: seine Altzeilen lassen sich
 * dann nicht mehr zuordnen, und die App sagt das, statt eine Haelfte zu raten.
 */
export const legacyPartyByCustomer = (
    split: ProjectSplit | null | undefined,
): Map<string, string> => {
    const count = new Map<string, number>();
    for (const p of split?.participants ?? []) {
        count.set(p.customerId, (count.get(p.customerId) ?? 0) + 1);
    }
    const out = new Map<string, string>();
    for (const p of split?.participants ?? []) {
        if (count.get(p.customerId) === 1) out.set(p.customerId, p.partyId);
    }
    return out;
};

/** Der Teilnehmer im Klartext: "Haus 3" bzw. der Auftraggeber, wenn kein Abschnitt. */
export const participantLabel = (p: ProjectSplitParticipant): string =>
    (p.label || '').trim() || p.customerId;

/**
 * Der Ausschnitt, den EIN Teilnehmer von den Vorbelegen des Projekts sehen
 * darf.
 *
 * Der Abzug der Vorrechnungen ist die gefaehrlichste Stelle des ganzen Moduls:
 * zieht ein Beleg zu viel ab, fordert er fast nichts — und sieht dabei richtig
 * aus. Bis SQL Nr. 54 reichte dafuer der Auftraggeber. Bei vier Haeusern
 * DESSELBEN Auftraggebers reicht er nicht mehr.
 */
export interface PartyScope {
    partyId: string;
    customerId: string;
    /**
     * Hat dieser Auftraggeber im Schluessel GENAU EINEN Teilnehmer?
     *
     * Nur dann darf ein Altbeleg ohne Teilnehmerstempel diesem Teilnehmer
     * zugerechnet werden. Hat er mehrere Abschnitte, ist der Altbeleg zwischen
     * ihnen nicht zu entscheiden — dann wird gesperrt und nicht geraten.
     */
    legacyUnique: boolean;
}

export const partyScopeFor = (
    split: ProjectSplit,
    participant: ProjectSplitParticipant,
): PartyScope => ({
    partyId: participant.partyId,
    customerId: participant.customerId,
    legacyUnique:
        split.participants.filter((p) => p.customerId === participant.customerId).length === 1,
});

/**
 * Vorbelege auf EINEN Teilnehmer eingrenzen — und benennen, was sich nicht
 * eingrenzen laesst.
 *
 * Drei Faelle, und der dritte ist der Grund fuer diese Funktion:
 *   Stempel passt        gehoert diesem Teilnehmer
 *   Stempel passt nicht  gehoert einem anderen, faellt weg
 *   kein Stempel         eindeutig -> gehoert ihm; sonst `ambiguous`
 *
 * `ambiguous` wird vom Aufrufer zur SPERRE gemacht und nie stillschweigend
 * weggelassen: ein weggelassener Abzug laesst den Kunden doppelt zahlen, ein
 * bei allen mitgezaehlter Abzug laesst die Gruppe zu wenig fordern. Beides
 * faellt an keiner einzelnen Zahl auf.
 */
export const scopeToParty = <T extends { splitPartyId?: string; customerId?: string }>(
    docs: readonly T[],
    scope: PartyScope,
): { own: T[]; ambiguous: T[] } => {
    const own: T[] = [];
    const ambiguous: T[] = [];
    for (const doc of docs ?? []) {
        const stamped = (doc.splitPartyId || '').trim();
        if (stamped) {
            if (stamped === scope.partyId) own.push(doc);
            continue;
        }
        if ((doc.customerId || '').trim() !== scope.customerId) continue;
        if (scope.legacyUnique) own.push(doc);
        else ambiguous.push(doc);
    }
    return { own, ambiguous };
};

// ═══════════════════════════════════════════════════════════════════════════
// Pruefung
// ═══════════════════════════════════════════════════════════════════════════

export type SplitIssueCode =
    | 'no_participants'
    | 'duplicate_customer'
    | 'share_out_of_range'
    | 'sum_mismatch'
    | 'unassigned_billed';

export interface SplitIssue {
    code: SplitIssueCode;
    message: string;
    serviceId?: string;
    customerId?: string;
    /** Der Teilnehmer, an dem der Befund haengt (project_splits.id). */
    partyId?: string;
}

/** 5000 -> "50,00 %". Nur fuer Meldungen. */
export const formatShare = (bp: number): string =>
    `${(bp / 100).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} %`;

/**
 * Pruefsumme je Position: genau 10000 Basispunkte, ohne Toleranz.
 *
 * Eine Position, die auf 9000 kommt, verschenkt 10 % dauerhaft — und zwar
 * lautlos: jeder einzelne Beleg sieht richtig aus, nur ihre Summe fordert zu
 * wenig. Ein Anteil ueber 100 % ist die Doppelforderung derselben
 * Fehlerklasse. Beides ist ein Fehler und keine Warnung.
 *
 * Geprueft wird gegen die uebergebenen Positionen und nicht gegen die
 * eingetragenen Ausnahmen: eine Position ganz ohne Ausnahme fiele sonst aus
 * der Pruefung, obwohl gerade sie die Grundquote traegt.
 */
export const validateSplit = (
    serviceIds: readonly string[],
    split: ProjectSplit,
): SplitIssue[] => {
    const issues: SplitIssue[] = [];
    const participants = split.participants ?? [];

    if (participants.length === 0) {
        return [
            {
                code: 'no_participants',
                message: 'Der Aufteilungsschlüssel nennt keinen Auftraggeber.',
            },
        ];
    }

    // Zwei verschiedene Dubletten, und nur die zweite ist neu:
    //
    //   dieselbe partyId       — zwei Zeilen, die dieselbe Datenbankzeile
    //                            meinen; der Abgleich schriebe die eine mit der
    //                            anderen zu und der Rest verschwaende lautlos.
    //   Auftraggeber + Label   — derselbe Auftraggeber im selben Abschnitt. Er
    //                            bekaeme zwei Teilrechnungen ueber denselben
    //                            Anteil. Derselbe Auftraggeber in VERSCHIEDENEN
    //                            Abschnitten ist dagegen der ganze Zweck dieser
    //                            Erweiterung und ausdruecklich erlaubt.
    const seenParty = new Set<string>();
    const seenPair = new Set<string>();
    for (const p of participants) {
        if (seenParty.has(p.partyId)) {
            issues.push({
                code: 'duplicate_customer',
                customerId: p.customerId,
                partyId: p.partyId,
                message: 'Derselbe Teilnehmer steht mehrfach im Aufteilungsschlüssel.',
            });
        }
        seenParty.add(p.partyId);

        const pair = `${p.customerId}|${(p.label || '').trim()}`;
        if (seenPair.has(pair)) {
            issues.push({
                code: 'duplicate_customer',
                customerId: p.customerId,
                partyId: p.partyId,
                message: (p.label || '').trim()
                    ? `Derselbe Auftraggeber steht im Abschnitt „${p.label}" mehrfach.`
                    : 'Derselbe Auftraggeber steht mehrfach im Aufteilungsschlüssel. Sollen es mehrere Abschnitte sein, braucht jeder einen eigenen Namen.',
            });
        }
        seenPair.add(pair);

        for (const bp of [p.defaultShareBp, ...Object.values(p.overrides ?? {})]) {
            if (!Number.isInteger(bp) || bp < 0 || bp > SHARE_FULL_BP) {
                issues.push({
                    code: 'share_out_of_range',
                    customerId: p.customerId,
                    partyId: p.partyId,
                    message: `Ungültiger Anteil (${bp}). Erlaubt sind ganze Basispunkte von 0 bis ${SHARE_FULL_BP}.`,
                });
            }
        }
    }

    for (const serviceId of serviceIds) {
        const sum = participants.reduce((acc, p) => acc + shareBpFor(p, serviceId), 0);
        if (sum !== SHARE_FULL_BP) {
            issues.push({
                code: 'sum_mismatch',
                serviceId,
                message: `Die Anteile dieser Position ergeben ${formatShare(sum)} statt 100,00 %.`,
            });
        }
    }

    return issues;
};

// ═══════════════════════════════════════════════════════════════════════════
// Teilen ohne Verlust
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ganzzahlen nach dem Verfahren des groessten Restes aufteilen.
 *
 * Die Summe der Teile ist EXAKT die Ausgangszahl — das ist der ganze Zweck.
 * Naiv gerundet fehlte bei einem Stueck auf drei Auftraggeber ein Tausendstel,
 * und genau solche Reste tauchen spaeter als "erbracht, nie abgerechnet"
 * wieder auf.
 *
 * Die Reihenfolge der Restvergabe ist FEST (groesster Rest, bei Gleichstand
 * die kleinere Teilnehmerkennung). Ohne feste Ordnung lieferte derselbe Beleg beim
 * Neuaufbau einen anderen Cent — und ein Beleg, der sich beim Ansehen
 * veraendert, ist kein Beleg.
 *
 * Das Vorzeichen wird vor der Aufteilung abgespalten und danach wieder
 * angeheftet: Storno und Gutschrift tragen negative Mengen und muessen exakt
 * dieselbe Aufteilung erzeugen wie der Beleg, den sie aufheben. Ueber den
 * Betrag geteilt kaeme sonst ein anderer Rest heraus als beim Original, und
 * die Ruecknahme liesse eine Einheit stehen.
 */
export const splitUnits = (
    total: number,
    shares: ReadonlyMap<string, number>,
): Map<string, number> => {
    const out = new Map<string, number>();
    if (shares.size === 0) return out;

    const sumBp = [...shares.values()].reduce((a, b) => a + b, 0);
    if (sumBp !== SHARE_FULL_BP) {
        throw new Error(
            `Aufteilung nicht möglich: die Anteile ergeben ${formatShare(sumBp)} statt 100,00 %.`,
        );
    }

    const sign = total < 0 ? -1 : 1;
    const magnitude = Math.abs(Math.round(total));

    const entries = [...shares.entries()].sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
    const remainders: { partyId: string; remainder: number }[] = [];
    let assigned = 0;
    for (const [partyId, bp] of entries) {
        const exact = magnitude * bp;
        const base = Math.floor(exact / SHARE_FULL_BP);
        assigned += base;
        out.set(partyId, base);
        remainders.push({ partyId, remainder: exact % SHARE_FULL_BP });
    }

    let rest = magnitude - assigned;
    remainders.sort((a, b) =>
        b.remainder !== a.remainder ? b.remainder - a.remainder : a.partyId < b.partyId ? -1 : 1,
    );
    for (const r of remainders) {
        if (rest <= 0) break;
        out.set(r.partyId, (out.get(r.partyId) ?? 0) + 1);
        rest -= 1;
    }

    if (sign < 0) for (const [k, v] of out) out.set(k, -v);
    return out;
};

/**
 * Eine Menge aufteilen. Summe der Teilmengen == Ausgangsmenge, exakt.
 *
 * Gerechnet wird auf dem Hundertstel-Raster — der Form, in der die Menge auf
 * dem Beleg steht (siehe QUANTITY_GRID). Liegt die Ausgangsmenge nicht darauf,
 * faellt der Schnitt aufs Tausendstel zurueck (QUANTITY_GRID_FINE): lieber
 * eine krumme Teilmenge als eine Summe, die nicht mehr aufgeht.
 */
export const splitQuantity = (
    quantity: number,
    shares: ReadonlyMap<string, number>,
): Map<string, number> => {
    const grid = gridFor(quantity || 0);
    const units = splitUnits(Math.round((quantity || 0) * grid), shares);
    const out = new Map<string, number>();
    for (const [partyId, u] of units) out.set(partyId, u / grid);
    return out;
};

/** Dieselbe Aufteilung fuer Geldbetraege in Cent. */
export const splitCents = (cents: number, shares: ReadonlyMap<string, number>): Map<string, number> =>
    splitUnits(Math.round(cents || 0), shares);

/**
 * Zeilenarten, die geteilt werden.
 *
 * Abgeleitete Zeilen (Nachlass, Zuschlag, Abschlagsabzug) stehen bewusst NICHT
 * darin: sie entstehen je Beleg neu aus dessen eigener Summe
 * (applyDocumentDiscount / applyAncillaryCosts). Mitgeteilt ergaeben sie einen
 * Zuschlag auf einen bereits geteilten Zuschlag.
 */
export const SPLITTABLE_LINE_KINDS: ReadonlySet<InvoiceLine['kind']> = new Set<InvoiceLine['kind']>([
    'SERVICE',
]);

/**
 * Die Zeilen eines Belegs auf die Teilnehmer aufteilen.
 *
 * Geschluesselt nach TEILNEHMER (partyId): mehrere Abschnitte desselben
 * Auftraggebers sind verschiedene Belege, und ueber den Auftraggeber
 * geschluesselt fielen sie in einen Topf — der zweite Abschnitt ueberschriebe
 * den ersten, und dessen Leistung verschwaende ohne eine Zahl, die falsch
 * aussieht.
 *
 * Geteilt wird die MENGE, nicht der Betrag. Der Einzelpreis bleibt der
 * vereinbarte — er ist die Groesse, an der Projekt-LV, Leistungsnachweis und
 * Deckungspruefung messen. Und die E-Rechnung verlangt, dass das Zeilennetto
 * aus Menge mal Einzelpreis hervorgeht; ein geteilter Betrag bei voller Menge
 * fiele durch jede Pruefung des Empfaengers.
 *
 * Folge fuer den Andruck: der Beleg nennt die geteilte Menge. Das ist zugleich
 * die Bedingung dafuer, dass sich der eigene Teilbeleg spaeter wieder EINLESEN
 * laesst — drei Belege ueber je die volle Menge liessen sich nicht mehr zu
 * einem Stand verrechnen.
 *
 * KUMULATIV ist `billedStands` Pflicht. Die Zeile traegt dann in
 * previouslyBilledQty, wie viel von ihrer Menge schon abgerechnet ist — und
 * das ist bei einer Teilrechnung der Stand DIESES Auftraggebers, nicht der des
 * Projekts. Der Wert ist nicht bloss Anzeige: die Reservierungs-Sicht klemmt
 * daran (greatest(quantity - previouslyBilledQty, 0)), und die Pruefung
 * uncoveredPriorBilling misst daran die Deckung. Mit dem Gesamtstand bestueckt
 * reservierte der zweite Teilbeleg zu wenig — und gab damit Menge frei, die
 * ein dritter Entwurf ein zweites Mal beansprucht haette.
 */
export const splitLines = (
    lines: readonly InvoiceLine[],
    split: ProjectSplit,
    billedStands?: ReadonlyMap<string, BilledStand>,
): Map<string, InvoiceLine[]> => {
    const out = new Map<string, InvoiceLine[]>();
    for (const p of split.participants) out.set(p.partyId, []);

    for (const line of lines) {
        // Ueberschriften und Erlaeuterungen tragen keine Menge und gehoeren
        // jedem Beleg unveraendert. Sie zu teilen ergaebe nichts, sie
        // wegzulassen naehme dem Beleg seine Gliederung.
        if (!SPLITTABLE_LINE_KINDS.has(line.kind)) {
            for (const list of out.values()) list.push({ ...line });
            continue;
        }

        const shares = new Map<string, number>();
        for (const p of split.participants) {
            const bp = shareBpFor(p, line.serviceId);
            if (bp > 0) shares.set(p.partyId, bp);
        }
        const quantities = splitQuantity(line.quantity ?? 0, shares);

        for (const [partyId, quantity] of quantities) {
            // Anteil vorhanden, Menge aber auf null gerundet: die Zeile faellt
            // fuer diesen Teilnehmer weg. Eine Zeile ueber 0 fordert nichts.
            if (Math.abs(quantity) <= SPLIT_EPSILON) continue;
            const next: InvoiceLine = {
                ...line,
                quantity,
                splitShareBp: shares.get(partyId),
                splitFullQuantity: line.quantity,
            };
            // Nur anfassen, wenn die Zeile den Wert ueberhaupt fuehrt:
            // gesetzt-und-0 heisst "kumulativ, nichts vorabgerechnet",
            // fehlend heisst "fortschreibend". Den Unterschied darf das Teilen
            // nicht einebnen.
            if (billedStands && next.previouslyBilledQty !== undefined) {
                next.previouslyBilledQty = billedFor(
                    billedStands.get(line.serviceId ?? ''),
                    partyId,
                );
            }
            out.get(partyId)?.push(next);
        }
    }
    return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// Was jedem Teilnehmer bereits berechnet wurde
// ═══════════════════════════════════════════════════════════════════════════

export interface BilledStand {
    /** Abgerechnete Menge je TEILNEHMER (partyId), nicht je Auftraggeber. */
    byParty: Map<string, number>;
    /**
     * Abgerechnete Menge, die sich KEINEM Teilnehmer zuordnen laesst.
     *
     * Zwei Herkuenfte, und beide bedeuten dasselbe:
     *   - Altbestand vor dem Schluessel: gebucht oder eingelesen ohne jeden
     *     Stempel.
     *   - Eine Zeile, die nur den Auftraggeber traegt, waehrend dieser
     *     Auftraggeber MEHRERE Abschnitte hat — dann ist sie zwischen ihnen
     *     nicht mehr zu entscheiden.
     *
     * Sie einem Teilnehmer zuzuschlagen waere geraten; sie zu ignorieren
     * rechnete sie ein zweites Mal ab. Deshalb steht sie getrennt und sperrt
     * die Aufteilung, bis jemand sie zuordnet.
     */
    unassigned: number;
}

/**
 * Traegt der Eintrag eine ABRECHNUNG (und nicht eine erbrachte Leistung)?
 *
 * Zwei Herkuenfte zaehlen, und nur diese beiden:
 *   BU-…   Buchung dieser App beim Ausstellen (negativ beim Storno)
 *   ALT-…  aus einer Alt-Rechnung eingelesener Stand
 *
 * VZ- (Verzicht) und RN- (Ruecknahme) sind ueber isAccountingEntry mit
 * erfasst; sie tragen Menge 0 und veraendern keine Summe.
 */
const isBillingEntry = (t: ServiceTransaction): boolean =>
    Boolean(t?.serviceId) && (isAccountingEntry(t) || isBackfilledAltEntry(t));

/**
 * Abrechnungsstand je Leistung UND Teilnehmer, aus dem Leistungsprotokoll.
 *
 * Abgeleitet und NICHT zusaetzlich gespeichert. Ein zweiter Zaehler neben
 * quantityBilled waere die naechste Groesse, die auseinanderlaeuft — und dieses
 * Modul entsteht, weil genau das schon einmal Geld gekostet hat. Das Protokoll
 * ist ohnehin die Quelle, aus der jede Pruefung rueckwaerts liest.
 *
 * Verrechnet, nicht bloss addiert: eine negative Zeile ist die Ruecknahme
 * (Storno) oder die Korrektur eines zu hoch eingelesenen Altbestands und muss
 * den Stand mindern. Erst summieren, dann bei 0 kappen — so haengt das
 * Ergebnis nicht von der Reihenfolge der Zeilen ab.
 *
 * `legacy` loest Zeilen auf, die nur den Auftraggeber tragen (alles von vor
 * SQL Nr. 54). Ohne die Karte — oder wenn dieser Auftraggeber mehrere
 * Abschnitte hat — landet die Menge unter `unassigned` und sperrt. Das
 * Protokoll selbst wird dafuer NIE umgeschrieben: es ist Beweismittel, und ein
 * nachtraeglich gestempelter Eintrag waere keines mehr.
 */
export const billedByParty = (
    log: readonly ServiceTransaction[],
    legacy: ReadonlyMap<string, string> = new Map(),
): Map<string, BilledStand> => {
    const out = new Map<string, BilledStand>();
    for (const t of log ?? []) {
        if (!isBillingEntry(t)) continue;
        const quantity = t.quantity || 0;
        if (quantity === 0) continue;

        const stand = out.get(t.serviceId) ?? {
            byParty: new Map<string, number>(),
            unassigned: 0,
        };
        const partyId = resolveParty(t, legacy);
        if (partyId) {
            stand.byParty.set(partyId, (stand.byParty.get(partyId) ?? 0) + quantity);
        } else {
            stand.unassigned += quantity;
        }
        out.set(t.serviceId, stand);
    }

    for (const stand of out.values()) {
        for (const [partyId, value] of [...stand.byParty]) {
            stand.byParty.set(partyId, Math.max(0, value));
        }
        stand.unassigned = Math.max(0, stand.unassigned);
    }
    return out;
};

/**
 * Nur die aus ALT-RECHNUNGEN eingelesene Deckung, je Teilnehmer.
 *
 * Gegenstueck zu billedByParty und aus demselben Protokoll: dort steht der
 * Stand des Leistungsverzeichnisses, hier die Menge, fuer die es einen BELEG
 * gibt. Die Differenz beider ist genau die manuelle Verbuchung ohne Beleg —
 * der Befund, den previouslyInvoicedQty sichtbar macht. Waeren beide dieselbe
 * Zahl, verlore die Pruefung ihren Sinn.
 */
export const altCoveredByParty = (
    log: readonly ServiceTransaction[],
    legacy: ReadonlyMap<string, string> = new Map(),
): Map<string, BilledStand> =>
    billedByParty((log ?? []).filter((t) => isBackfilledAltEntry(t)), legacy);

/** Der Stand EINES Teilnehmers an EINER Position. */
export const billedFor = (stand: BilledStand | undefined, partyId: string): number =>
    stand?.byParty.get(partyId) ?? 0;

// ═══════════════════════════════════════════════════════════════════════════
// Kumulativ mit Anteil
// ═══════════════════════════════════════════════════════════════════════════

export interface CumulativeGrowth {
    /** Was DIESER Beleg fuer DIESEN Auftraggeber bucht. Nie negativ. */
    growth: number;
    /**
     * Zu viel abgerechnet — der Anteil wurde nach einer Teilabrechnung
     * gesenkt.
     *
     * Das darf NICHT stillschweigend auf null geklemmt werden. Genau das tut
     * die ungeteilte Rechnung heute (Math.max(0, …) in planProjectIssue), und
     * bei einem einzelnen Auftraggeber ist das auch richtig: dort kann der
     * Stand nicht sinken. Mit Anteilen kann er es — und der Auftraggeber
     * bekaeme sein Geld dann nie zurueck, ohne dass irgendwo etwas auffiele.
     * Der Aufrufer muss daraus eine Gutschrift machen oder abbrechen.
     */
    overBilled: number;
}

/**
 * Der kumulative Zuwachs EINES Auftraggebers an EINER Position.
 *
 *   soll = Anteil x Gesamtstand      was er insgesamt zu tragen hat
 *   ist  = sein bisheriger Stand     was er schon bezahlt hat
 *
 * Der Gesamtstand ist ungeteilt und bleibt es: das Projekt hat EINEN
 * Leistungsstand, geteilt wird die Forderung daraus.
 */
export const cumulativeGrowthFor = (params: {
    /** Erbrachte Menge des Projekts bis zum Stichtag. */
    standTotal: number;
    shareBp: number;
    /** Bisheriger Abrechnungsstand DIESES Auftraggebers. */
    billed: number;
}): CumulativeGrowth => {
    const soll = (params.standTotal * params.shareBp) / SHARE_FULL_BP;
    const delta = soll - params.billed;
    if (delta < -SPLIT_EPSILON) return { growth: 0, overBilled: -delta };
    return { growth: Math.max(0, delta), overBilled: 0 };
};

/**
 * Derselbe Riegel fuer Belege OHNE LV-Bezug — die Abschlagsrechnung.
 *
 * Ihre Zeile ist eine Pauschalposition ohne serviceId, groupCoverageIssues
 * greift dort also ins Leere. Geprueft wird stattdessen der Anteil am
 * BELEGKOPF: die Abschlaege einer Aufteilung muessen zusammen 100 % tragen.
 *
 * Ohne diese Pruefung liesse sich ein Geschwisterbeleg loeschen und die
 * uebrigen einzeln ausstellen — die Gruppe forderte dauerhaft weniger, und
 * jeder einzelne Beleg saehe dabei richtig aus.
 */
export const groupShareIssue = (shares: readonly (number | undefined)[]): SplitIssue | null => {
    const sum = shares.reduce<number>((acc, bp) => acc + (bp ?? 0), 0);
    if (sum === SHARE_FULL_BP) return null;
    return {
        code: 'sum_mismatch',
        message: `Die Belege dieser Aufteilung tragen zusammen ${formatShare(sum)} statt 100,00 %.`,
    };
};

/**
 * Altbestand nachtraeglich einem Auftraggeber zuordnen.
 *
 * Der Weg aus der Sperre: bevor es den Aufteilungsschluessel gab, trugen die
 * Abrechnungszeilen keinen Kunden. In einem Bestandsprojekt ist damit ALLES
 * Altbestand ohne Zuordnung — und unassignedBilledIssues sperrt jede
 * Teilrechnung, bis jemand entscheidet.
 *
 * Entschieden wird hier, nicht geraten: der Aufrufer nennt den Auftraggeber,
 * der die bisherigen Rechnungen bekommen hat. Das ist bei einem Projekt, das
 * erst jetzt geteilt wird, eine Angabe, die der Betrieb hat — und die
 * niemand sonst haben kann.
 *
 * Angefasst werden AUSSCHLIESSLICH Zeilen ohne Kunden. Eine bereits
 * zugeordnete Zeile umzuschreiben hiesse, eine Forderung von einem
 * Auftraggeber auf einen anderen zu verschieben; das ist nie ein Nebeneffekt
 * einer Zuordnung, sondern ein eigener Vorgang mit eigenem Beleg.
 */
export const assignUnassignedBilled = (
    log: readonly ServiceTransaction[],
    /**
     * Der Teilnehmer, dem der Altbestand gehoert. BEIDE Felder werden
     * gestempelt: `splitPartyId` ist die Zuordnung, `customerId` bleibt die
     * Angabe, die jede Auswertung ausserhalb der Aufteilung liest (Debitor,
     * DATEV, Buchhaltungsansicht).
     */
    party: { partyId: string; customerId: string },
    /**
     * Dieselbe Karte wie in billedByParty. Sie entscheidet, was ueberhaupt als
     * "nicht zugeordnet" gilt — ohne sie gaelte jede Zeile eines bestehenden,
     * nach Auftraggebern geteilten Projekts als frei und wuerde umgeschrieben.
     * Das waere die Verschiebung einer Forderung von einem Auftraggeber auf
     * einen anderen, und die ist nie ein Nebeneffekt einer Zuordnung.
     */
    legacy: ReadonlyMap<string, string> = new Map(),
    /** Nur diese Positionen; ohne Angabe alle. */
    serviceIds?: readonly string[],
): { log: ServiceTransaction[]; assigned: Map<string, number> } => {
    const nur = serviceIds ? new Set(serviceIds) : null;
    const assigned = new Map<string, number>();
    const next = (log ?? []).map((t) => {
        // Angefasst wird nur, was sich NICHT aufloesen laesst — dieselbe
        // Bedingung, an der unassignedBilledIssues sperrt. Eine Zeile, die nur
        // den Auftraggeber traegt, gehoert genau dann dazu, wenn dieser
        // Auftraggeber mehrere Abschnitte hat; sonst ist sie eindeutig und
        // bleibt unberuehrt.
        if (!isBillingEntry(t) || resolveParty(t, legacy)) return t;
        if (nur && !nur.has(t.serviceId)) return t;
        assigned.set(t.serviceId, (assigned.get(t.serviceId) ?? 0) + (t.quantity || 0));
        return { ...t, splitPartyId: party.partyId, customerId: party.customerId };
    });
    return { log: next, assigned };
};

/**
 * Der Riegel vor dem Ausstellen: deckt die GRUPPE zusammen 100 % ab?
 *
 * Eine halbe Teilrechnungs-Gruppe ist der gefaehrlichste Zustand des ganzen
 * Moduls. Sie entsteht, wenn die Anlage der Belege mittendrin abbricht und
 * auch das Aufraeumen scheitert — dann liegen zwei von drei Entwuerfen da, und
 * jeder von ihnen ist fuer sich vollstaendig richtig. Nur ihre Summe fordert
 * zwei Drittel, und das faellt niemandem auf: es gibt keine Zahl, die falsch
 * aussieht.
 *
 * Deshalb wird beim Ausstellen nicht der einzelne Beleg geprueft, sondern die
 * Gruppe. Gerechnet wird ueber die ZEILEN aller Gruppenbelege, nicht ueber den
 * Anteil am Belegkopf: bei positionsweiser Aufteilung traegt der Kopf gar
 * keinen, und die Aussage haengt ohnehin je Position.
 *
 * Geprueft werden nur die Positionen des eigenen Belegs. Was die Geschwister
 * sonst noch abrechnen, geht diesen Beleg nichts an — und eine Position, die
 * hier gar nicht vorkommt, darf ihn nicht aufhalten.
 */
export const groupCoverageIssues = (
    serviceIds: readonly string[],
    groupLines: readonly InvoiceLine[],
): SplitIssue[] => {
    const byService = new Map<string, number>();
    for (const l of groupLines) {
        if (l.kind !== 'SERVICE' || !l.serviceId) continue;
        byService.set(l.serviceId, (byService.get(l.serviceId) ?? 0) + (l.splitShareBp ?? 0));
    }
    const issues: SplitIssue[] = [];
    for (const serviceId of serviceIds) {
        const sum = byService.get(serviceId) ?? 0;
        if (sum !== SHARE_FULL_BP) {
            issues.push({
                code: 'sum_mismatch',
                serviceId,
                message: `Die Belege dieser Aufteilung decken zusammen ${formatShare(sum)} statt 100,00 %.`,
            });
        }
    }
    return issues;
};

/**
 * Der Riegel vor dem Ausstellen: nicht zugeordneter Altbestand.
 *
 * Solange an einer Position abgerechnete Menge ohne Auftraggeber steht, laesst
 * sich der Anteil eines Teilnehmers nicht ausrechnen — jede Antwort waere
 * geraten. Lieber gar kein Beleg als einer, dessen Forderung auf einer
 * Vermutung steht.
 */
export const unassignedBilledIssues = (
    serviceIds: readonly string[],
    log: readonly ServiceTransaction[],
    legacy: ReadonlyMap<string, string> = new Map(),
): SplitIssue[] => {
    const stands = billedByParty(log, legacy);
    const issues: SplitIssue[] = [];
    for (const serviceId of serviceIds) {
        const stand = stands.get(serviceId);
        if (stand && stand.unassigned > SPLIT_EPSILON) {
            issues.push({
                code: 'unassigned_billed',
                serviceId,
                message: `${stand.unassigned} Einheiten sind bereits abgerechnet, aber keinem Teilnehmer der Aufteilung zugeordnet. Die Aufteilung ließe sich nur raten.`,
            });
        }
    }
    return issues;
};
